import * as XLSX from 'xlsx';
import { format, isValid } from 'date-fns';

export interface BulkOrderRow {
  internalId: string;
  orderId: string;
  receiverName: string;
  receiverPhone: string;
  receiverEmail?: string;
  address: string;
  address2?: string;
  landmark: string;
  pincode: string;
  city?: string;
  state?: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  productName: string;
  quantity: number;
  invoiceValue: number;
  paymentMode: 'COD' | 'Prepaid';
  pickupDate?: string;
  sku?: string;
  hsn?: string;
  altPhone?: string;
  addressNickname: string;
  pickupContactName?: string;
  pickupPhone?: string;
  pickupAddress?: string;
  pickupAddress2?: string;
  pickupLandmark?: string;
  pickupPincode?: string;
  pickupCity?: string;
  pickupState?: string;
}

export interface ValidationError {
  column: keyof BulkOrderRow;
  message: string;
}

export interface ValidatedBulkOrder extends BulkOrderRow {
  errors: ValidationError[];
  isValid: boolean;
  origin_address?: any;
}

/**
 * Converts Excel Serial Date to JS Date String
 */
export const formatExcelDate = (value: any): string => {
  if (!value) return '';
  
  let date: Date;

  if (value instanceof Date) {
    date = value;
  } else {
    const num = Number(value);
    if (!isNaN(num)) {
      // Excel serial date 46112.22...
      date = new Date(Math.round((num - 25569) * 86400 * 1000));
    } else {
      date = new Date(String(value));
    }
  }

  if (!isValid(date)) return String(value);

  return format(date, 'MMMM d yyyy h:mma');
};

export const parseBulkFile = async (file: File): Promise<BulkOrderRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);

        // Map column names to our interface
        const rows = json.map((row: any) => ({
          internalId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
          orderId: String(row['Order ID'] || row['orderId'] || '').trim(),
          receiverName: String(
            row['Receiver Name'] || row['receiverName'] || ''
          ).trim(),
          receiverPhone: String(
            row['Receiver Phone'] || row['receiverPhone'] || ''
          ).trim(),
          receiverEmail: (() => {
            const raw = String(row['Receiver Email'] || row['receiverEmail'] || '').trim();
            return /^(na|n\/a|none|nil|-)$/i.test(raw) ? '' : raw;
          })(),
          address: String(row['Address'] || row['address'] || '').trim(),
          address2: String(row['Address 2'] || row['address2'] || '').trim(),
          landmark: String(row['Landmark'] || row['landmark'] || '').trim(),
          pincode: String(row['Pincode'] || row['pincode'] || '').trim(),
          city: String(row['City'] || row['city'] || '').trim(),
          state: String(row['State'] || row['state'] || '').trim(),
          weight: Number(row['Weight (kg)'] || row['weight'] || 0),
          length: Number(row['Length (cm)'] || row['length'] || 0),
          width: Number(row['Width (cm)'] || row['width'] || 0),
          height: Number(row['Height (cm)'] || row['height'] || 0),
          productName: String(
            row['Product Name'] || row['productName'] || ''
          ).trim(),
          quantity: Number(row['Quantity'] || row['quantity'] || 1),
          invoiceValue: Number(
            row['Invoice Value'] || row['invoiceValue'] || 0
          ),
          paymentMode:
            String(row['Payment Mode'] || row['paymentMode'] || 'Prepaid')
              .trim()
              .toUpperCase() === 'COD'
              ? 'COD'
              : 'Prepaid',
          pickupDate: formatExcelDate(row['Pickup Date'] || row['pickupDate']),
          sku: String(row['SKU'] || row['sku'] || '').trim(),
          hsn: String(row['HSN'] || row['hsn'] || '').trim(),
          altPhone: String(row['Alt Phone'] || row['altPhone'] || '').trim(),
          addressNickname: String(row['Address Nickname'] || row['addressNickname'] || '').trim(),
          pickupContactName: String(row['Pickup Contact Name'] || row['pickupContactName'] || '').trim(),
          pickupPhone: String(row['Pickup Phone'] || row['pickupPhone'] || '').trim(),
          pickupAddress: String(row['Pickup Address'] || row['pickupAddress'] || '').trim(),
          pickupAddress2: String(row['Pickup Address 2'] || row['pickupAddress2'] || '').trim(),
          pickupLandmark: String(row['Pickup Landmark'] || row['pickupLandmark'] || '').trim(),
          pickupPincode: String(row['Pickup Pincode'] || row['pickupPincode'] || '').trim(),
          pickupCity: String(row['Pickup City'] || row['pickupCity'] || '').trim(),
          pickupState: String(row['Pickup State'] || row['pickupState'] || '').trim(),
        }));

        resolve(rows as BulkOrderRow[]);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsBinaryString(file);
  });
};

export const validateBulkOrders = (
  rows: BulkOrderRow[]
): ValidatedBulkOrder[] => {
  return rows.map((row) => {
    const errors: ValidationError[] = [];

    if (!row.orderId) errors.push({ column: 'orderId', message: 'Order ID is required' });
    if (!row.addressNickname) errors.push({ column: 'addressNickname', message: 'Address Nickname is required' });
    if (!row.receiverName)
      errors.push({ column: 'receiverName', message: 'Receiver Name is required' });

    if (!row.receiverPhone) {
      errors.push({ column: 'receiverPhone', message: 'Receiver Phone is required' });
    } else if (!/^[0-9]{10}$/.test(row.receiverPhone)) {
      errors.push({
        column: 'receiverPhone',
        message: 'Invalid Phone Format (10 digits)',
      });
    }

    if (row.receiverEmail && !/\S+@\S+\.\S+/.test(row.receiverEmail)) {
      errors.push({ column: 'receiverEmail', message: 'Invalid Email Format' });
    }

    if (!row.address) errors.push({ column: 'address', message: 'Address 1 is required' });
    if (!row.landmark) errors.push({ column: 'landmark', message: 'Landmark is required' });
    if (!row.city) errors.push({ column: 'city', message: 'City is required' });
    if (!row.state) errors.push({ column: 'state', message: 'State is required' });

    if (!row.pincode) {
      errors.push({ column: 'pincode', message: 'Pincode is required' });
    } else if (!/^[0-9]{6}$/.test(row.pincode)) {
      errors.push({ column: 'pincode', message: 'Invalid Pincode Format (6 digits)' });
    }

    if (row.pickupPincode && !/^[0-9]{6}$/.test(row.pickupPincode)) {
      errors.push({ column: 'pickupPincode', message: 'Invalid Pickup Pincode Format (6 digits)' });
    }

    if (!row.pickupContactName) errors.push({ column: 'pickupContactName', message: 'Pickup Contact Name is required' });
    if (!row.pickupPhone) {
      errors.push({ column: 'pickupPhone', message: 'Pickup Phone is required' });
    } else if (!/^[0-9]{10}$/.test(row.pickupPhone)) {
      errors.push({ column: 'pickupPhone', message: 'Invalid Pickup Phone Format (10 digits)' });
    }
    if (!row.pickupAddress) errors.push({ column: 'pickupAddress', message: 'Pickup Address is missing or invalid' });
    if (!row.pickupLandmark) errors.push({ column: 'pickupLandmark', message: 'Pickup Landmark is missing or invalid' });
    if (!row.pickupCity) errors.push({ column: 'pickupCity', message: 'Pickup City is missing or invalid' });
    if (!row.pickupState) errors.push({ column: 'pickupState', message: 'Pickup State is missing or invalid' });
    if (!row.pickupPincode) {
      errors.push({ column: 'pickupPincode', message: 'Pickup Pincode is required' });
    }

    if (row.weight <= 0)
      errors.push({ column: 'weight', message: 'Weight must be > 0' });
    if (row.length <= 0)
      errors.push({ column: 'length', message: 'Length must be > 0' });
    if (row.width <= 0)
      errors.push({ column: 'width', message: 'Width must be > 0' });
    if (row.height <= 0)
      errors.push({ column: 'height', message: 'Height must be > 0' });

    if (!row.productName) {
      errors.push({ column: 'productName', message: 'Product Name is required' });
    } else if (!/[a-zA-Z]/.test(row.productName)) {
      errors.push({ column: 'productName', message: 'Product Name cannot be only numbers' });
    }
    if (row.quantity <= 0)
      errors.push({ column: 'quantity', message: 'Quantity must be > 0' });
    if (row.invoiceValue <= 0)
      errors.push({ column: 'invoiceValue', message: 'Invoice Value must be > 0' });

    // Build row-level origin_address if pickup address fields are specified
    let origin_address: any = undefined;
    if (row.pickupAddress || row.pickupPincode) {
      origin_address = {
        contact_name: row.pickupContactName || 'Warehouse',
        company_name: row.addressNickname || row.pickupContactName || 'Warehouse',
        address1: row.pickupAddress || '',
        address2: row.pickupAddress2 || '',
        landmark: row.pickupLandmark || '',
        pincode: row.pickupPincode || '',
        city: row.pickupCity || '',
        state: row.pickupState || '',
        contact_no: row.pickupPhone || '',
      };
    }

    return {
      ...row,
      origin_address,
      errors,
      isValid: errors.length === 0,
    };
  });
};

